import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { THEMES, useTheme } from './themeConfig'; 
import { 
  FaChevronLeft
} from 'react-icons/fa';
import './App.css';

import { MediaPlaybackProvider } from './context/MediaPlaybackContext';
import { FeedbackProvider, useFeedback } from './context/FeedbackContext';
import {
  PAGE_EXPERIENCES,
  analyzeStory,
} from './services/giftBuilderConfig';
import { countPageMedia } from './services/mediaLimits';
import { DEFAULT_TEMPLATES } from './services/templates';
import useAdminSession from './hooks/useAdminSession';
import useGiftActions from './hooks/useGiftActions';
import useGiftPage, { createTemplateModules } from './hooks/useGiftPage';
import { getEditablePlaceholder, getEditableTextValue, isTemplatePromptValue } from './utils/editingPlaceholders';

// Components
import AdminLoginModal from './components/AdminLoginModal';
import DeleteGiftModal from './components/DeleteGiftModal';
import ForcePasswordChangeModal from './components/ForcePasswordChangeModal';
import GiftEditor from './components/GiftEditor';
import GiftSetPendingBindModal from './components/GiftSetPendingBindModal';
import GiftSetSettingsModal from './components/GiftSetSettingsModal';
import GiftVisualEffects from './components/GiftVisualEffects';
import GiftViewer from './components/GiftViewer';
import OpeningExperienceRenderer from './components/OpeningExperienceRenderer';
import OpeningCoverEditor from './components/OpeningCoverEditor';
import PrivacyPolicyModal from './components/PrivacyPolicyModal';
import ShareCardModal from './components/ShareCardModal';
import UploadStatusOverlay from './components/UploadStatusOverlay';

const TEMPLATE_INTRO_PROMPTS = [
  'Ava & Julian',
  'A little favor from our day',
  'Thank you for being here. Open the story, sign the blessing wall, and keep this as a tiny piece of tonight.',
  'Mom',
  'For Mom',
  'A few small ways to say thank you',
  'Bestie',
  'The Map of Us',
  'A tiny archive of our photos, places, jokes, and chaos',
];

const GlobalStyles = () => (
  <style dangerouslySetInnerHTML={{ __html: `
    * { box-sizing: border-box; -webkit-touch-callout: none !important; -webkit-user-select: none; user-select: none; -webkit-tap-highlight-color: transparent; }
    html, body, #root { max-width: 100%; overflow-x: hidden; }
    input, textarea, .admin-input { -webkit-user-select: text !important; user-select: text !important; }
    html, body { overscroll-behavior-y: contain; scroll-behavior: smooth; margin: 0; padding: 0; }
    .no-scrollbar::-webkit-scrollbar { display: none; }
    
    input, textarea, select, [contenteditable="true"], .module-title { 
      color: color-mix(in srgb, var(--theme-input-text) 68%, #7c8591) !important;
      font-size: 0.92rem !important;
      font-weight: 400 !important;
      letter-spacing: 0 !important;
      line-height: 1.55 !important;
    }
    input:focus, textarea:focus, select:focus, [contenteditable="true"]:focus, .module-title:focus {
      color: var(--theme-input-text) !important;
    }
    input::placeholder, textarea::placeholder {
      color: color-mix(in srgb, var(--theme-muted) 72%, #9aa2ad) !important;
      font-weight: 400 !important;
    }
    [class*="text-[#5A4A6B]"] { color: var(--theme-text) !important; }
    .text-pink-100, .text-pink-200, .text-pink-300, .text-pink-400, .text-pink-500, .text-pink-600 { color: var(--theme-accent) !important; }
    .bg-pink-50, .bg-pink-100 { background-color: var(--theme-accent-soft) !important; }
    .bg-pink-200, .bg-pink-300, .bg-pink-400, .bg-pink-500 { background-color: var(--theme-accent) !important; }
    .border-pink-50, .border-pink-100, .border-pink-200, .border-pink-300 { border-color: var(--theme-accent-muted) !important; }
    .shadow-pink-100, .shadow-pink-200 { --tw-shadow-color: var(--theme-accent-muted) !important; --tw-shadow: var(--tw-shadow-colored) !important; }
    
    input, textarea, select, button { max-width: 100%; }
    img, video, canvas { max-width: 100%; }
    [data-module-type] { overflow-wrap: break-word; }

    .bg-white input, .bg-white textarea, .bg-white .module-title {
      color: color-mix(in srgb, var(--theme-input-text) 68%, #7c8591) !important;
    }

    @keyframes flow {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    .animate-flow { background-size: 200% 200%; animation: flow 6s ease infinite; }

    @keyframes pulse-glow {
      0% { box-shadow: 0 0 0 0 rgba(244, 114, 182, 0.4); }
      70% { box-shadow: 0 0 0 20px rgba(244, 114, 182, 0); }
      100% { box-shadow: 0 0 0 0 rgba(244, 114, 182, 0); }
    }
    .header-glow { animation: pulse-glow 3s infinite; }
  `}} />
);

const AppContent = () => {
  const { theme, currentThemeId, setCurrentThemeId } = useTheme(); 
  const feedback = useFeedback();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showGiftSetSettings, setShowGiftSetSettings] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({ active: false, progress: 0, type: 'media' });
  const [replayOpening, setReplayOpening] = useState(false);
  const [showShareCard, setShowShareCard] = useState(false);
  const [focusedModuleId, setFocusedModuleId] = useState('');
  const [activeEditorPanel, setActiveEditorPanel] = useState('overview');

  const {
    params,
    pageData,
    setPageData,
    isLoading,
    setIsLoading,
    pageError,
    isLocked,
    setIsLocked,
    hasSeenOpening,
    giftSetContext,
    applyTemplate,
    resetGiftPage,
  } = useGiftPage({ setCurrentThemeId });

  const {
    isEditing,
    setIsEditing,
    isPreviewMode,
    setIsPreviewMode,
    isAdminModalOpen,
    setIsAdminModalOpen,
    showPasswordChange,
    setShowPasswordChange,
    handlePressStart,
    handlePressEnd,
    handleAdminVerify: verifyAdminSession,
    handlePasswordChanged,
  } = useAdminSession({
    pageId: params.nfcId,
    canOpenAdmin: !giftSetContext?.isLinkedMember,
    adminBlockedMessage: 'This NFC is a member entry. Open the owner NFC to edit or manage the Gift Set.',
  });

  const moduleEndRef = useRef(null);
  const uploadNoticeTimerRef = useRef(null);

  const handleAdminSessionExpired = useCallback(() => {
    setIsEditing(false);
    setIsPreviewMode(false);
    setShowPasswordChange(false);
    setFocusedModuleId('');
    setActiveEditorPanel('overview');
    setIsAdminModalOpen(true);
  }, [setIsAdminModalOpen, setIsEditing, setIsPreviewMode, setShowPasswordChange]);

  const handleGiftDeleted = useCallback(() => {
    resetGiftPage();
    setIsEditing(false);
    setIsPreviewMode(false);
    setShowDeleteConfirm(false);
    setDeleteInput('');
    setFocusedModuleId('');
    setReplayOpening(false);
    setShowShareCard(false);
    setShowPrivacyPolicy(false);
    setShowGiftSetSettings(false);
    setShowPasswordChange(false);
    setIsAdminModalOpen(false);
    setActiveEditorPanel('overview');
  }, [
    resetGiftPage,
    setIsAdminModalOpen,
    setIsEditing,
    setIsPreviewMode,
    setShowPasswordChange,
  ]);

  const contentPageId = giftSetContext?.contentPageId || params.nfcId;

  const updateModuleConfig = useCallback((moduleId, patch) => {
    setPageData((prev) => ({
      ...prev,
      modules: (prev.modules || []).map((module) => {
        const currentModuleId = module.id || module._localId;
        if (currentModuleId !== moduleId) return module;
        return {
          ...module,
          data: {
            ...(module.data || {}),
            ...patch,
          },
        };
      }),
    }));
  }, [setPageData]);

  const {
    saveGift,
    deleteGift,
    addModule,
    updateModule,
    removeModule,
    updateStoryCoachAnswer,
    updateSharingMode,
    applyStoryCoach,
    applyAllStoryCoach,
  } = useGiftActions({
    pageId: contentPageId,
    pageData,
    setPageData,
    currentThemeId,
    setIsLoading,
    setIsLocked,
    setIsEditing,
    setIsPreviewMode,
    moduleEndRef,
    deleteInput,
    isUploadActive: uploadStatus.active,
    onAdminSessionExpired: handleAdminSessionExpired,
    onGiftDeleted: handleGiftDeleted,
  });

  useEffect(() => {
    const handleUploadStatus = (event) => {
      clearTimeout(uploadNoticeTimerRef.current);
      const hasError = Boolean(event.detail?.error);
      const progress = Math.round(event.detail?.progress || 0);
      setUploadStatus({
        active: Boolean(event.detail?.active),
        progress,
        stage: event.detail?.stage || '',
        type: event.detail?.type || 'media',
        fileName: event.detail?.fileName || '',
        error: event.detail?.error || '',
      });
      if (hasError || (!event.detail?.active && progress >= 100)) {
        uploadNoticeTimerRef.current = setTimeout(() => {
          setUploadStatus((prev) => ({
            ...prev,
            active: false,
            error: '',
            progress: 0,
            stage: '',
            fileName: '',
          }));
        }, hasError ? 5500 : 1800);
      }
    };

    window.addEventListener('lumirae-upload-status', handleUploadStatus);
    return () => {
      clearTimeout(uploadNoticeTimerRef.current);
      window.removeEventListener('lumirae-upload-status', handleUploadStatus);
    };
  }, []);

  const handlePresetChange = async (e) => {
    const templateKey = e.target.value;
    if (!templateKey) return;

    const selectedTemplate = DEFAULT_TEMPLATES[templateKey];
    const templateData = selectedTemplate?.data || {};
    const hasModules = Array.isArray(pageData.modules) && pageData.modules.length > 0;
    const hasDefaultModules = Array.isArray(selectedTemplate?.modules) && selectedTemplate.modules.length > 0;

    if (!hasModules && hasDefaultModules) {
      setPageData((prev) => ({
        ...prev,
        templateKey,
        themeId: templateData.themeId || prev.themeId,
        themeName: templateData.themeName || prev.themeName,
        giftFormat: templateData.giftFormat || prev.giftFormat || 'layer_by_layer',
        openingMode: templateData.openingMode || prev.openingMode || 'layered_gift',
        modules: createTemplateModules(templateKey, 'restored'),
      }));
      if (templateData.themeId) setCurrentThemeId(templateData.themeId);
      feedback.toast({
        title: 'Default Chapters Restored',
        message: 'The selected gift type now has its starter modules again.',
        type: 'success',
      });
      return;
    }

    const hasStartedGift = Boolean(
      pageData.ownerName ||
      pageData.openingTitle ||
      pageData.openingSubtitle ||
      hasModules
    );

    if (hasStartedGift) {
      const shouldApply = await feedback.confirm({
        title: 'Apply Preset?',
        message: 'This replaces the current story structure and starter copy. Uploaded files already saved in storage are not deleted by this action.',
        confirmLabel: 'Apply Preset',
        cancelLabel: 'Keep Current',
        type: 'warning',
      });
      if (!shouldApply) {
        return;
      }
    }

    applyTemplate(templateKey);
  };

  const handleAdminVerify = (success) => {
    if (verifyAdminSession(success)) {
      setReplayOpening(false); 
    }
  };

  const activeEditing = isEditing && !isPreviewMode;
  const modules = useMemo(() => pageData.modules || [], [pageData.modules]);
  const focusedModuleIndex = modules.findIndex((module) => (module.id || module._localId) === focusedModuleId);
  const focusModuleByIndex = (nextIndex) => {
    if (nextIndex < 0 || nextIndex >= modules.length) return;
    setActiveEditorPanel('modules');
    setFocusedModuleId(modules[nextIndex].id || modules[nextIndex]._localId);
  };
  const focusPreviousModule = () => focusModuleByIndex(focusedModuleIndex - 1);
  const focusNextModule = () => focusModuleByIndex(focusedModuleIndex + 1);
  const clearFocusedModule = () => setFocusedModuleId('');
  const focusModule = (moduleId) => {
    setActiveEditorPanel('modules');
    setFocusedModuleId(moduleId);
  };
  const moveModule = useCallback((moduleId, direction) => {
    setActiveEditorPanel('modules');
    setPageData((prev) => {
      const currentModules = Array.isArray(prev.modules) ? [...prev.modules] : [];
      const fromIndex = currentModules.findIndex((module) => (module.id || module._localId) === moduleId);
      const toIndex = fromIndex + direction;
      if (fromIndex < 0 || toIndex < 0 || toIndex >= currentModules.length) return prev;
      const [movingModule] = currentModules.splice(fromIndex, 1);
      currentModules.splice(toIndex, 0, movingModule);
      return {
        ...prev,
        modules: currentModules,
      };
    });
  }, [setPageData]);

  const handleThemeSelect = useCallback((themeId) => {
    const nextTheme = THEMES[themeId];
    setCurrentThemeId(themeId);
    setPageData((prev) => ({
      ...prev,
      themeId,
      themeName: nextTheme?.name || themeId,
    }));
  }, [setCurrentThemeId, setPageData]);

  useEffect(() => {
    if (!focusedModuleId) return;
    const hasFocusedModule = modules.some((module) => (module.id || module._localId) === focusedModuleId);
    if (!hasFocusedModule) {
      setFocusedModuleId('');
    }
  }, [focusedModuleId, modules]);

  useEffect(() => {
    if (!focusedModuleId) return;
    const timer = window.setTimeout(() => {
      document.getElementById(`gift-module-${focusedModuleId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [focusedModuleId]);

  if (isLoading) return <div className="h-screen flex items-center justify-center" style={{ backgroundColor: theme.bg }}><div className="h-10 w-10 animate-spin rounded-full border-4 border-white border-t-transparent" style={{ borderTopColor: theme.accent }} /></div>;

  if (pageError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: theme.bg, color: theme.text }}>
        <GlobalStyles />
        <div className="w-full max-w-md rounded-2xl border-4 bg-white p-8 text-center shadow-2xl" style={{ borderColor: theme.cardBorder }}>
          <h1 className="text-2xl font-black uppercase tracking-tight mb-4">Page not found</h1>
          <p className="text-sm font-bold opacity-70">{pageError}</p>
        </div>
      </div>
    );
  }

  const openingMode = pageData.openingMode || 'layered_gift';
  const pageExperience = PAGE_EXPERIENCES[openingMode] || PAGE_EXPERIENCES.layered_gift;
  const isWeddingFavorPublic = !activeEditing && pageData.themeId === 'wedding_favor';
  const storyAnalysis = analyzeStory(pageData);
  const handleSealGift = async () => {
    const issues = Array.isArray(storyAnalysis?.issues) ? storyAnalysis.issues : [];
    if (issues.length > 0) {
      const shouldContinue = await feedback.confirm({
        type: 'warning',
        title: 'Seal with unfinished parts?',
        message: `${issues.slice(0, 3).join(' ')}${issues.length > 3 ? ` And ${issues.length - 3} more item${issues.length - 3 === 1 ? '' : 's'}.` : ''} You can still seal now if this is intentional.`,
        confirmLabel: 'Seal Anyway',
        cancelLabel: 'Keep Editing',
      });
      if (!shouldContinue) return;
    }
    await saveGift(true);
  };
  const mediaUsage = countPageMedia(pageData);
  const shouldUseOpeningExperience = !activeEditing && openingMode !== 'full_page' && (!hasSeenOpening || replayOpening);
  const effectiveOpeningMode = shouldUseOpeningExperience ? openingMode : 'full_page';
  const updateGiftIntroField = (field, value) => {
    setPageData((prev) => ({
      ...prev,
      [field]: value,
      [`${field}Touched`]: true,
    }));
  };

  const getGiftIntroInputProps = (field, fallbackPlaceholder) => {
    const touched = Boolean(pageData[`${field}Touched`]);
    const isTemplateValue = isTemplatePromptValue(pageData[field], TEMPLATE_INTRO_PROMPTS);
    return {
      value: touched && !isTemplateValue ? getEditableTextValue(pageData[field], TEMPLATE_INTRO_PROMPTS) : '',
      placeholder: getEditablePlaceholder(fallbackPlaceholder, pageData[field], TEMPLATE_INTRO_PROMPTS),
      onChange: (event) => updateGiftIntroField(field, event.target.value),
    };
  };

  const giftIntroEditor = (
    <section className="lumirae-editor-panel p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-[9px] font-black uppercase tracking-[0.28em]" style={{ color: theme.accent }}>
            Step 1 / Opening Cover
          </p>
          <h2 className="mt-1 text-xl font-black uppercase tracking-tight" style={{ color: theme.text }}>
            Edit the first thing they see
          </h2>
        </div>
        <span className="rounded-full px-3 py-1 text-[8px] font-black uppercase tracking-widest" style={{ backgroundColor: 'var(--lumirae-accent-soft)', color: theme.accent }}>
          Visible to recipient
        </span>
      </div>
      <div className="grid gap-3">
        <label className="block">
          <span className="mb-1 block text-[8px] font-black uppercase tracking-widest opacity-50">Recipient name</span>
          <input
            className="admin-input w-full rounded-[1.1rem] border bg-white/80 px-4 py-3 text-sm font-semibold outline-none"
            style={{ borderColor: 'var(--lumirae-border-default)' }}
            maxLength={48}
            {...getGiftIntroInputProps('ownerName', 'For Mom')}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[8px] font-black uppercase tracking-widest opacity-50">Opening title</span>
          <input
            className="admin-input w-full rounded-[1.1rem] border bg-white/80 px-4 py-3 text-sm font-medium outline-none"
            style={{ borderColor: 'var(--lumirae-border-default)' }}
            maxLength={72}
            {...getGiftIntroInputProps('openingTitle', 'A Gift Is Waiting')}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[8px] font-black uppercase tracking-widest opacity-50">Opening message</span>
          <textarea
            className="admin-input min-h-[120px] w-full resize-none rounded-[1.1rem] border bg-white/80 px-4 py-3 text-sm font-medium leading-relaxed outline-none"
            style={{ borderColor: 'var(--lumirae-border-default)' }}
            maxLength={180}
            {...getGiftIntroInputProps('openingSubtitle', 'Open slowly. This was made for one person, one moment, and one feeling.')}
          />
        </label>
      </div>
      <OpeningCoverEditor
        pageData={pageData}
        setPageData={setPageData}
        pageId={contentPageId}
        mediaUsage={mediaUsage}
        theme={theme}
      />
    </section>
  );

  return (
    <div className="min-h-screen relative font-sans transition-colors duration-300 overflow-x-hidden" 
      style={{
        backgroundColor: theme.bg,
        color: theme.text,
        '--theme-text': theme.text,
        '--theme-muted': theme.secondaryText || theme.textMuted,
        '--theme-input-text': theme.inputText,
        '--theme-accent': theme.accent,
        '--theme-accent-soft': theme.accentSoft || theme.inputBg,
        '--theme-accent-muted': theme.accentMuted || theme.cardBorder,
        '--lumirae-bg-app': theme.bg,
        '--lumirae-surface-base': theme.surface || theme.cardBg,
        '--lumirae-surface-raised': theme.surfaceRaised || theme.cardBg,
        '--lumirae-text-primary': theme.text,
        '--lumirae-text-secondary': theme.secondaryText || theme.textMuted,
        '--lumirae-accent-primary': theme.accent,
        '--lumirae-accent-soft': theme.accentSoft || theme.inputBg,
        '--lumirae-border-default': theme.cardBorder || theme.borderDefault,
        '--lumirae-state-error': theme.stateError || '#B42318',
        '--lumirae-state-success': theme.stateSuccess || '#28734D',
        '--lumirae-radius-sm': `${theme.radius?.sm || 10}px`,
        '--lumirae-radius-md': `${theme.radius?.md || 18}px`,
        '--lumirae-radius-lg': `${theme.radius?.lg || 28}px`,
        '--lumirae-radius-xl': `${theme.radius?.xl || 40}px`,
        '--lumirae-radius-full': `${theme.radius?.full || 999}px`
      }}
      onMouseDown={handlePressStart} onMouseUp={handlePressEnd}
      onTouchStart={handlePressStart} onTouchEnd={handlePressEnd}>
      
      <GlobalStyles />
      <GiftVisualEffects />

      <UploadStatusOverlay uploadStatus={uploadStatus} theme={theme} />

      <GiftSetPendingBindModal
        currentUuid={params.nfcId}
        giftSetContext={giftSetContext}
        onBound={() => window.location.reload()}
        theme={theme}
      />
      
      <AdminLoginModal isOpen={isAdminModalOpen} onClose={() => setIsAdminModalOpen(false)} onVerify={handleAdminVerify} pageId={params.nfcId} />

      <ForcePasswordChangeModal
        isOpen={showPasswordChange}
        pageId={params.nfcId}
        onSuccess={handlePasswordChanged}
        onCancel={() => setShowPasswordChange(false)}
      />

      <ShareCardModal
        isOpen={showShareCard}
        onClose={() => setShowShareCard(false)}
        pageData={pageData}
        storyAnalysis={storyAnalysis}
      />

      {isPreviewMode && (
        <motion.button 
          initial={{ y: -50 }} animate={{ y: 20 }}
          onClick={() => setIsPreviewMode(false)}
          className="fixed top-5 right-5 z-[3000] px-6 py-3 bg-white/90 backdrop-blur-md rounded-full shadow-2xl border-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest"
          style={{ borderColor: theme.accent, color: theme.accent }}
        >
          <FaChevronLeft /> Back to Edit
        </motion.button>
      )}

      <DeleteGiftModal
        isOpen={showDeleteConfirm}
        deleteInput={deleteInput}
        onDeleteInputChange={setDeleteInput}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={deleteGift}
        theme={theme}
      />

      <GiftSetSettingsModal
        isOpen={showGiftSetSettings}
        ownerUuid={params.nfcId}
        onClose={() => setShowGiftSetSettings(false)}
        theme={theme}
      />
      
      <main
        aria-label="Gift page"
        className={`w-full mx-auto p-4 md:p-8 ${isWeddingFavorPublic ? 'lumirae-wedding-public-shell' : ''} ${activeEditing ? 'lumirae-builder-editing pb-28' : 'pb-10'} ${pageExperience.shell}`}
      >
        {activeEditing ? (
          <>
            <header className={`lumirae-builder-phone-shell ${focusedModuleId ? 'lumirae-builder-phone-shell--focused' : ''} mb-8 p-4 border-4 relative overflow-hidden rounded-[3rem] ${pageExperience.header} animate-flow`}
              style={{ backgroundColor: theme.cardBg, borderColor: theme.cardBorder, boxShadow: `16px 16px 0px 0px ${theme.cardShadow}` }}>
              <div className="relative z-10">
                <GiftEditor
                  pageData={pageData}
                  setPageData={setPageData}
                  isLocked={isLocked}
                  theme={theme}
                  storyAnalysis={storyAnalysis}
                  onSealGift={handleSealGift}
                  onPreviewGift={() => setIsPreviewMode(true)}
                  onPresetChange={handlePresetChange}
                  onStoryCoachAnswer={updateStoryCoachAnswer}
                  onApplyStoryCoach={applyStoryCoach}
                  onApplyAllStoryCoach={applyAllStoryCoach}
                  onAddModule={addModule}
                  onThemeSelect={handleThemeSelect}
                  onSharingModeChange={updateSharingMode}
                  uploadStatus={uploadStatus}
                  focusedModuleId={focusedModuleId}
                  onFocusModule={focusModule}
                  focusedModuleIndex={focusedModuleIndex}
                  focusedModuleCount={modules.length}
                  onFocusPrevious={focusPreviousModule}
                  onFocusNext={focusNextModule}
                  onDoneFocus={clearFocusedModule}
                  onUpdateModuleConfig={updateModuleConfig}
                  onRemoveModule={removeModule}
                  onMoveModule={moveModule}
                  activePanel={activeEditorPanel}
                  onPanelChange={setActiveEditorPanel}
                  giftIntroContent={giftIntroEditor}
                  onSaveDraft={() => saveGift()}
                  onChangePassword={() => setShowPasswordChange(true)}
                  onDeleteGift={() => setShowDeleteConfirm(true)}
                  onGiftSetSettings={() => setShowGiftSetSettings(true)}
                  onOpenPrivacy={() => setShowPrivacyPolicy(true)}
                />
              </div>
            </header>

            {activeEditorPanel === 'modules' && (
              <>
                <OpeningExperienceRenderer
                  modules={pageData.modules}
                  isEditing={activeEditing}
                  updateModule={updateModule}
                  removeModule={removeModule}
                  pageId={contentPageId}
                  isLocked={isLocked}
                  openingMode={effectiveOpeningMode}
                  mediaUsage={mediaUsage}
                  focusedModuleId={focusedModuleId}
                  pageData={pageData}
                  updateModuleConfig={updateModuleConfig}
                  theme={theme}
                />
                <div ref={moduleEndRef} />
              </>
            )}

            <PrivacyPolicyModal
              isOpen={showPrivacyPolicy}
              onAccept={() => setShowPrivacyPolicy(false)}
              onReject={() => setShowPrivacyPolicy(false)}
              infoOnly
            />
          </>
        ) : (
          <GiftViewer
            pageData={pageData}
            pageId={contentPageId}
            pageExperience={pageExperience}
            storyAnalysis={storyAnalysis}
            theme={theme}
            activeEditing={activeEditing}
            isLocked={isLocked}
            openingMode={openingMode}
            effectiveOpeningMode={effectiveOpeningMode}
            hasSeenOpening={hasSeenOpening}
            replayOpening={replayOpening}
            showPrivacyPolicy={showPrivacyPolicy}
            onUpdateModule={updateModule}
            onRemoveModule={removeModule}
            onReplayOpening={() => setReplayOpening(true)}
            onExitReplay={() => setReplayOpening(false)}
            onOpenShareCard={() => setShowShareCard(true)}
            onOpenPrivacy={() => setShowPrivacyPolicy(true)}
            onClosePrivacy={() => setShowPrivacyPolicy(false)}
            moduleEndRef={moduleEndRef}
          />
        )}
      </main>

    </div>
  );
};

const App = () => (
  <MediaPlaybackProvider>
    <FeedbackProvider>
      <AppContent />
    </FeedbackProvider>
  </MediaPlaybackProvider>
);

export default App;
